import { StatCard } from '@/components/StatCard';
import { TableSkeleton } from '@/components/TableSkeleton';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  PaginationNext,
  PaginationPrevious,
} from '@/components/ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConsumptionAlerts, useConsumptionAlertSummary } from '@/hooks/useConsumptionAlerts';
import { useExportConsumptionAlerts } from '@/hooks/useConsumptionAlertActions';
import type { ConsumptionAlert } from '@octave/contracts';
import { useCurrentOrganization } from '@/hooks/useOrganization';
import { useTenantSlug } from '@/hooks/useTenantSlug';
import {
  consumptionAlertDotClasses,
  consumptionAlertEventTypeMapping,
  consumptionAlertIconClasses,
  consumptionAlertPillClasses,
  consumptionAlertSeverity,
} from '@/lib/consumptionAlertMapping';
import { useDebouncedValue } from '@mantine/hooks';
import {
  Ban,
  BellRing,
  ChevronRight,
  Download,
  Gauge,
  Loader2,
  Search,
  TrendingDown,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ThresholdsExplainer } from './consumption-alerts/ThresholdsExplainer';
import { formatDateTime } from '@/lib/format';

const MONTHS = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: currentYear - 2019 }, (_, i) => 2020 + i);

function parsePositiveInt(
  raw: string | null,
  fallback: number,
  max?: number
): number {
  const value = Number(raw);
  if (!Number.isInteger(value) || value < 1) return fallback;
  if (max !== undefined && value > max) return fallback;
  return value;
}

function formatOccurredAt(occurredAt: string): string {
  return formatDateTime(occurredAt);
}

/** Share of the plan reached, when the operator reported the numbers. */
function UsageLevel({
  alert,
  severity,
}: {
  alert: ConsumptionAlert;
  severity: 'blocked' | 'warning';
}) {
  if (alert.percent === null && !alert.consumption) return null;

  const barColor = severity === 'blocked' ? 'bg-red-500' : 'bg-amber-500';
  const textColor = severity === 'blocked' ? 'text-red-700' : 'text-amber-700';

  return (
    <div className="w-full">
      <div className="flex items-baseline justify-between gap-2">
        {alert.percent !== null && (
          <span className={`text-sm font-semibold tabular-nums ${textColor}`}>
            {alert.percent} %
          </span>
        )}
        {alert.consumption && (
          <span className="text-[11px] text-slate-500 tabular-nums truncate">
            {alert.consumption}
            {alert.threshold ? ` / ${alert.threshold}` : ''}
          </span>
        )}
      </div>
      {alert.percent !== null && (
        <div className="mt-1 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
          <div
            className={`h-1.5 rounded-full ${barColor}`}
            style={{ width: `${Math.min(alert.percent, 100)}%` }}
          />
        </div>
      )}
    </div>
  );
}

export function ConsumptionAlertsView() {
  const { data: organization } = useCurrentOrganization();
  const { tenantSlug } = useTenantSlug();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = parsePositiveInt(searchParams.get('page'), 1);
  const limit = parsePositiveInt(searchParams.get('limit'), 20, 100);

  const [localSearchText, setLocalSearchText] = useState('');
  const [debouncedSearchText] = useDebouncedValue(localSearchText, 500);

  // Period filter (same options as the invoices view). Default: current month.
  const [dateFilter, setDateFilter] = useState<string>('this-month');
  const [fromMonth, setFromMonth] = useState<{
    month: number;
    year: number;
  } | null>(null);
  const [monthRange, setMonthRange] = useState<{
    fromMonth: number;
    fromYear: number;
    toMonth: number;
    toYear: number;
  } | null>(null);

  const dateRange = useMemo((): { start: Date; end: Date } | null => {
    if (!dateFilter || dateFilter === 'all') return null;
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();

    if (dateFilter === 'from-month') {
      if (!fromMonth) return null;
      return {
        start: new Date(fromMonth.year, fromMonth.month, 1),
        end: now,
      };
    }

    if (dateFilter === 'month-range') {
      if (!monthRange) return null;
      return {
        start: new Date(monthRange.fromYear, monthRange.fromMonth, 1),
        end: new Date(monthRange.toYear, monthRange.toMonth + 1, 0),
      };
    }

    switch (dateFilter) {
      case 'this-month':
        return {
          start: new Date(year, month, 1),
          end: new Date(year, month + 1, 0),
        };
      case 'last-month':
        return {
          start: new Date(year, month - 1, 1),
          end: new Date(year, month, 0),
        };
      case 'last-3-months':
        return {
          start: new Date(year, month - 2, 1),
          end: new Date(year, month + 1, 0),
        };
      case 'last-6-months':
        return {
          start: new Date(year, month - 5, 1),
          end: new Date(year, month + 1, 0),
        };
      case 'this-year':
        return {
          start: new Date(year, 0, 1),
          end: new Date(year, 11, 31),
        };
      default:
        return null;
    }
  }, [dateFilter, fromMonth, monthRange]);

  // The backend window is inclusive on occurredAt (timestamp comparison), so
  // push the end bound to the last millisecond of its day.
  const from = dateRange ? dateRange.start.toISOString() : undefined;
  const to = dateRange
    ? new Date(
        dateRange.end.getFullYear(),
        dateRange.end.getMonth(),
        dateRange.end.getDate(),
        23,
        59,
        59,
        999
      ).toISOString()
    : undefined;

  const { data: summary } = useConsumptionAlertSummary({
    organizationId: organization?.id,
    from,
    to,
  });
  const { data, isLoading, isError } = useConsumptionAlerts({
    organizationId: organization?.id,
    page,
    limit,
    q: debouncedSearchText,
    from,
    to,
  });

  const { mutate: exportAlerts, isPending: isExporting } =
    useExportConsumptionAlerts({
      organizationId: organization?.id,
      q: debouncedSearchText,
      from,
      to,
    });

  const alerts = data?.alerts ?? [];
  const total = data?.pageInfo.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  const hasSearch = debouncedSearchText.trim().length > 0;
  const noAlertsInPeriod = summary
    ? summary.total === 0
    : total === 0 && !hasSearch;

  const goToPage = (nextPage: number) => {
    const params = new URLSearchParams(searchParams);
    if (nextPage <= 1) {
      params.delete('page');
    } else {
      params.set('page', String(nextPage));
    }
    setSearchParams(params, { replace: true });
  };

  const resetPage = () => {
    if (searchParams.has('page')) {
      const params = new URLSearchParams(searchParams);
      params.delete('page');
      setSearchParams(params, { replace: true });
    }
  };

  const handleSearchChange = (newSearchText: string) => {
    setLocalSearchText(newSearchText);
    resetPage();
  };

  // The file follows the filters on screen, all pages included.
  const handleExport = (format: 'xlsx' | 'csv') => {
    exportAlerts(
      { format },
      {
        onSuccess: () => toast.success('Export réussi !'),
        onError: () => toast.error("Échec de l'export des alertes"),
      }
    );
  };

  return (
    <div className="space-y-8">
      <div>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">
              Alertes de consommation
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Suivez les alertes de consommation de vos lignes
            </p>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={isExporting || total === 0}
                className="gap-1.5 border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                {isExporting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Download className="h-4 w-4" />
                )}
                Exporter
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => handleExport('xlsx')}>
                Excel (.xlsx)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')}>
                CSV (.csv)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Metric cards */}
        {summary && (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 mt-6">
            <StatCard
              icon={BellRing}
              iconBg="bg-emerald-50"
              iconColor="text-emerald-600"
              label="Alertes totales"
              value={summary.total}
            />
            <StatCard
              icon={Gauge}
              iconBg="bg-amber-50"
              iconColor="text-amber-600"
              label="Alerte 80 %"
              value={summary.warning}
            />
            <StatCard
              icon={TrendingDown}
              iconBg="bg-orange-50"
              iconColor="text-orange-600"
              label="Alerte débit réduit"
              value={summary.fairUse}
            />
            <StatCard
              icon={Ban}
              iconBg="bg-rose-50"
              iconColor="text-rose-600"
              label="Alerte blocage"
              value={summary.cutOff}
            />
          </div>
        )}

        <div className="mt-6">
          <ThresholdsExplainer />
        </div>

        <div className="mt-4">
          {isLoading || !organization ? (
            <TableSkeleton columns={4} />
          ) : isError ? (
            <div className="text-center py-12">
              <p className="text-red-500 text-lg">
                Erreur lors du chargement des alertes de consommation
              </p>
            </div>
          ) : (
            <div className="space-y-5">
              {/* Filters: search + period */}
              <div className="flex items-center gap-3 flex-wrap">
                <div className="relative w-full sm:flex-1 sm:max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Rechercher un numéro ou un collaborateur..."
                    value={localSearchText}
                    onChange={(event) => handleSearchChange(event.target.value)}
                    className="pl-9 bg-white border-slate-200"
                    aria-label="Rechercher un numéro ou un collaborateur"
                  />
                </div>

                {/* Date filter */}
                <Select
                  value={dateFilter}
                  onValueChange={(v) => {
                    setDateFilter(v);
                    resetPage();
                  }}
                >
                  <SelectTrigger
                    className="w-[180px] bg-white border-slate-200 text-sm"
                    aria-label="Période"
                  >
                    <SelectValue placeholder="Période" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="month-range">Plage mois/année</SelectItem>
                    <SelectItem value="all">Toutes les dates</SelectItem>
                    <SelectItem value="this-month">Ce mois-ci</SelectItem>
                    <SelectItem value="last-month">Mois dernier</SelectItem>
                    <SelectItem value="last-3-months">
                      3 derniers mois
                    </SelectItem>
                    <SelectItem value="last-6-months">
                      6 derniers mois
                    </SelectItem>
                    <SelectItem value="this-year">Cette année</SelectItem>
                    <SelectItem value="from-month">À partir de...</SelectItem>
                  </SelectContent>
                </Select>

                {/* From month selects */}
                {dateFilter === 'from-month' && (
                  <div className="flex items-center gap-2">
                    <Select
                      value={fromMonth ? `${fromMonth.month}` : ''}
                      onValueChange={(v) => {
                        setFromMonth((prev) => ({
                          month: Number(v),
                          year: prev?.year ?? currentYear,
                        }));
                        resetPage();
                      }}
                    >
                      <SelectTrigger
                        className="w-[130px] bg-white border-slate-200 text-sm"
                        aria-label="Mois de début"
                      >
                        <SelectValue placeholder="Mois" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => (
                          <SelectItem key={i} value={`${i}`}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={fromMonth ? `${fromMonth.year}` : ''}
                      onValueChange={(v) => {
                        setFromMonth((prev) => ({
                          month: prev?.month ?? 0,
                          year: Number(v),
                        }));
                        resetPage();
                      }}
                    >
                      <SelectTrigger
                        className="w-[100px] bg-white border-slate-200 text-sm"
                        aria-label="Année de début"
                      >
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map((y) => (
                          <SelectItem key={y} value={`${y}`}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Month range selects */}
                {dateFilter === 'month-range' && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <Select
                      value={monthRange ? `${monthRange.fromMonth}` : ''}
                      onValueChange={(v) => {
                        setMonthRange((prev) => ({
                          fromMonth: Number(v),
                          fromYear: prev?.fromYear ?? currentYear,
                          toMonth: prev?.toMonth ?? new Date().getMonth(),
                          toYear: prev?.toYear ?? currentYear,
                        }));
                        resetPage();
                      }}
                    >
                      <SelectTrigger
                        className="w-[120px] bg-white border-slate-200 text-sm"
                        aria-label="Mois de début"
                      >
                        <SelectValue placeholder="De mois" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => (
                          <SelectItem key={i} value={`${i}`}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={monthRange ? `${monthRange.fromYear}` : ''}
                      onValueChange={(v) => {
                        setMonthRange((prev) => ({
                          fromMonth: prev?.fromMonth ?? 0,
                          fromYear: Number(v),
                          toMonth: prev?.toMonth ?? new Date().getMonth(),
                          toYear: prev?.toYear ?? currentYear,
                        }));
                        resetPage();
                      }}
                    >
                      <SelectTrigger
                        className="w-[90px] bg-white border-slate-200 text-sm"
                        aria-label="Année de début"
                      >
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map((y) => (
                          <SelectItem key={y} value={`${y}`}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="text-xs text-slate-400">→</span>
                    <Select
                      value={monthRange ? `${monthRange.toMonth}` : ''}
                      onValueChange={(v) => {
                        setMonthRange((prev) => ({
                          fromMonth: prev?.fromMonth ?? 0,
                          fromYear: prev?.fromYear ?? currentYear,
                          toMonth: Number(v),
                          toYear: prev?.toYear ?? currentYear,
                        }));
                        resetPage();
                      }}
                    >
                      <SelectTrigger
                        className="w-[120px] bg-white border-slate-200 text-sm"
                        aria-label="Mois de fin"
                      >
                        <SelectValue placeholder="À mois" />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((m, i) => (
                          <SelectItem key={i} value={`${i}`}>
                            {m}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select
                      value={monthRange ? `${monthRange.toYear}` : ''}
                      onValueChange={(v) => {
                        setMonthRange((prev) => ({
                          fromMonth: prev?.fromMonth ?? 0,
                          fromYear: prev?.fromYear ?? currentYear,
                          toMonth: prev?.toMonth ?? new Date().getMonth(),
                          toYear: Number(v),
                        }));
                        resetPage();
                      }}
                    >
                      <SelectTrigger
                        className="w-[90px] bg-white border-slate-200 text-sm"
                        aria-label="Année de fin"
                      >
                        <SelectValue placeholder="Année" />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map((y) => (
                          <SelectItem key={y} value={`${y}`}>
                            {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>

              {noAlertsInPeriod ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
                    <BellRing className="h-7 w-7 text-slate-400" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Aucune alerte sur la période
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm">
                    Aucune alerte de consommation n'a été enregistrée sur la
                    période sélectionnée
                  </p>
                </div>
              ) : total === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                  <div className="flex h-16 w-16 items-center justify-center rounded-full bg-slate-100 mb-4">
                    <Search className="h-7 w-7 text-slate-400" />
                  </div>
                  <h3 className="text-base font-semibold text-slate-900">
                    Aucun résultat
                  </h3>
                  <p className="text-sm text-slate-500 mt-1 max-w-sm">
                    Aucune alerte ne correspond à votre recherche
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    {alerts.length === 0 && (
                      <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                        <BellRing className="h-10 w-10 mb-3 text-slate-300" />
                        <p className="text-sm font-medium">
                          Aucune alerte sur cette page
                        </p>
                      </div>
                    )}
                    {alerts.map((alert) => {
                      const severity = consumptionAlertSeverity[alert.eventType];
                      const SeverityIcon = severity === 'blocked' ? Ban : Gauge;
                      const clickable = !!alert.mobileLineId;
                      return (
                        <div
                          key={alert.id}
                          onClick={
                            clickable
                              ? () =>
                                  // Straight to the consumption tab: the
                                  // alert is about usage, not about the
                                  // line's administrative details.
                                  navigate(
                                    `/o/${tenantSlug}/lines/${alert.mobileLineId}?tab=consumption`
                                  )
                              : undefined
                          }
                          className={`flex items-center gap-4 bg-white rounded-xl px-5 py-4 transition-all duration-200 border border-transparent ${
                            clickable
                              ? 'group cursor-pointer hover:shadow-md hover:scale-[1.005] active:scale-[0.995] hover:border-emerald-100'
                              : ''
                          }`}
                        >
                          {/* Left icon */}
                          <div
                            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${consumptionAlertIconClasses[severity].background}`}
                          >
                            <SeverityIcon
                              className={`h-4 w-4 ${consumptionAlertIconClasses[severity].icon}`}
                            />
                          </div>

                          {/* Main info */}
                          <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(0,10rem)] gap-x-4 items-center">
                            {/* Col 1 — Number + holder + date */}
                            <div className="min-w-0">
                              <p className="text-sm font-semibold text-slate-900 tabular-nums tracking-wide">
                                {alert.lineNumber}
                              </p>
                              {alert.holderName && (
                                <p className="text-xs text-slate-600 truncate mt-0.5">
                                  {alert.holderName}
                                </p>
                              )}
                              <p className="text-xs text-slate-500 mt-0.5">
                                {formatOccurredAt(alert.occurredAt)}
                              </p>
                            </div>

                            {/* Col 2 — Type pill */}
                            <div className="min-w-0 mt-1.5 sm:mt-0 sm:flex sm:justify-start">
                              <span
                                title={
                                  consumptionAlertEventTypeMapping[
                                    alert.eventType
                                  ] ?? alert.eventType
                                }
                                className={`inline-flex max-w-full items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full border ${consumptionAlertPillClasses[severity]}`}
                              >
                                <span
                                  className={`h-1.5 w-1.5 shrink-0 rounded-full ${consumptionAlertDotClasses[severity]}`}
                                />
                                <span className="truncate">
                                  {consumptionAlertEventTypeMapping[
                                    alert.eventType
                                  ] ?? alert.eventType}
                                </span>
                              </span>
                            </div>

                            {/* Col 3 — Level reached */}
                            <div className="min-w-0 mt-1.5 sm:mt-0">
                              <UsageLevel alert={alert} severity={severity} />
                            </div>

                          </div>

                          {/* Chevron */}
                          {clickable && (
                            <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-emerald-500 shrink-0 transition-colors" />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-500">
                      {total} alerte{total > 1 ? 's' : ''} · Page {page} /{' '}
                      {totalPages}
                    </span>
                    <div className="flex items-center gap-2">
                      <PaginationPrevious
                        onClick={() => {
                          if (page > 1) goToPage(page - 1);
                        }}
                        aria-disabled={page <= 1}
                        className={
                          page <= 1 ? 'pointer-events-none opacity-50' : ''
                        }
                        aria-label="Page précédente"
                      />
                      <PaginationNext
                        onClick={() => {
                          if (page < totalPages) goToPage(page + 1);
                        }}
                        aria-disabled={page >= totalPages}
                        className={
                          page >= totalPages
                            ? 'pointer-events-none opacity-50'
                            : ''
                        }
                        aria-label="Page suivante"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
